import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useOrgBroker } from '@/models/broker'
import { BrokerAvatar } from './BrokerAvatar'
import { BrokerContactCard } from './BrokerContactCard'

/**
 * Floating bottom-right headshot that expands into the broker's contact card
 * when clicked. Renders nothing unless the org actually has a broker — see
 * `useOrgBroker`.
 *
 * The card is an absolutely-positioned child anchored to the same corner as the
 * avatar, so the two overlap and the surface looks like it grows out of the
 * headshot.
 */
export function BrokerWidget() {
  const { broker } = useOrgBroker()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (!broker) return null

  return (
    // cai-dock-clear: the product shell moves it off Cai's docked chat.
    <div
      ref={containerRef}
      className="cai-dock-clear fixed right-4 bottom-4 z-50 sm:right-6 sm:bottom-6 print:hidden"
    >
      <div className="relative size-12">
        <Button
          variant="iconOnly"
          aria-label={`Contact your Cai broker, ${broker.name}`}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'absolute inset-0 size-12 rounded-full p-0 shadow-lg ring-2 ring-white transition-all duration-200 ease-out',
            open ? 'scale-75 opacity-0' : 'scale-100 opacity-100 hover:scale-105'
          )}
        >
          <BrokerAvatar broker={broker} className="size-12" />
        </Button>

        <div
          // Faded out is not hidden: without `inert` the collapsed card's links
          // stay in the tab order and the a11y tree.
          inert={!open}
          className={cn(
            // Wide enough to keep the heading on one line next to the close button.
            'absolute right-0 bottom-0 w-[min(26rem,calc(100vw-2rem))] transition-all duration-200 ease-out',
            open ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2.5 opacity-0'
          )}
        >
          <BrokerContactCard
            broker={broker}
            className="ring-ink-100 shadow-xl ring-1"
            action={
              <Button
                variant="iconOnly"
                size="icon-sm"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="text-ink-400 hover:text-ink -mt-1 -mr-2"
              >
                <X />
              </Button>
            }
          />
        </div>
      </div>
    </div>
  )
}
